import {Component, Emitter, Object3D, property, Texture} from '@wonderlandengine/api';
import {formatString} from '../ui/utils/utils.js';
import {Localization} from '../utils/Localization.js';
import {NotifyPropertyChanged} from '../utils/NotifyPropertyChanged.js';
import {formatNumber} from '../utils/Utils.js';
import {RewardPickerBase} from './reward-picker.js';
import {RNG} from '../utils/rng.js';
import {AnimationController} from '@wonderlandengine/tweenjs';
import {BoardAnimations} from './animations/BoardAnimations.js';
import {AudioDirector} from './audio-director.js';
import {Sounds} from '../enums/Sounds.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {CursorTarget} from '@wonderlandengine/components';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {UnlockIcons} from '../ui/panels/unlocked-something.js';
import {Statistics} from './statistics.js';
import {UnlockRewardData} from '../types/UnlockManagerTypes.js';

export class GetDailyRewardParams
    extends RewardPickerBase
    implements NotifyPropertyChanged
{
    static TypeName = 'get-daily-reward-params';
    static InheritProperties = true;

    @property.object({required: true})
    cursorTargetObject: Object3D;

    propertyChanged: Emitter<[string]> = new Emitter();
    spriteName: string = '';
    mainTexture: string = '';
    awardNameString: string = '';
    descriptionString: string = '';

    showBlob: boolean = true;
    descriptionText: string = '';
    awardNameText: string = '';
    showCloseButton: boolean = false;
    awardIcon: Texture = null;

    private _coroutineManager: CoroutineManager = new CoroutineManager();

    private static _instance: GetDailyRewardParams;

    static get instance(): GetDailyRewardParams {
        return GetDailyRewardParams._instance;
    }

    private _animationController: AnimationController;

    init() {
        if (GetDailyRewardParams._instance) {
            console.error(
                'There can only be one instance of GetDailyRewardParams Component'
            );
        }
        GetDailyRewardParams._instance = this;
    }

    start(): void {
        this._animationController = this.animationObject.getComponent(AnimationController);
        this._animationController.addClip(
            BoardAnimations.instance.getAnimation('blob@idle'),
            'idle'
        );
        this._animationController.addClip(
            BoardAnimations.instance.getAnimation('blob@poke'),
            'poke'
        );
        this.descriptionText = '';
    }

    onActivate(): void {
        this._animationController.play('idle');
        this.cursorTargetObject.getComponent(CursorTarget).onClick.add(this._select);
    }

    onDeactivate(): void {
        this._animationController.stop('idle');
        this.cursorTargetObject.getComponent(CursorTarget).onClick.remove(this._select);
    }

    update(delta: number): void {
        this._coroutineManager.update(delta);
    }

    notifyPropertyChanged(propertyName: string): void {
        this.propertyChanged.notify(propertyName);
    }

    setUp(unlock: UnlockRewardData) {
        this.awardNameText = Localization.instance.get('RewardScreenDescriptionAlt');
        this.spriteName = unlock.icon;
        this.awardIcon = UnlockIcons.instance.getIcon(unlock.icon);
        this.awardNameString = formatString(
            Localization.instance.get(unlock.titleText),
            formatNumber(unlock.amount)
        );
        this.descriptionString = Localization.instance.get(unlock.descriptionText);
        if (unlock.addCumulatively == true) {
            Statistics.instance.addAnonymousPurchaseTransaction(
                unlock.transactionalStat,
                unlock.amount
            );
        } else {
            // hack redo look at this
            // only give if you havent gotten it
            const previousValue = Statistics.instance.getTransactional(
                unlock.transactionalStat
            );
            if (previousValue < unlock.amount) {
                Statistics.instance.addAnonymousPurchaseTransaction(
                    unlock.transactionalStat,
                    unlock.amount - previousValue
                );
            }
        }
    }

    reparent(parent: Object3D) {
        this.initialize(
            RNG.getItem([
                'Piece Green',
                'Piece Yellow',
                'Piece Pink',
                'Piece Orange',
                'Piece Red',
                'Piece Blue',
                'Piece Purple',
            ])
        );

        this.object.resetPosition();
        this.object.parent = parent;
        GetDailyRewardParams.instance.object.setPositionLocal([0, -0.1, 1.5]);
    }

    private *_showPresent() {
        AudioDirector.instance.playOneShot(Sounds.blobPopMax, true);

        //Instantiate(fx, getRewardButton.transform.position, Quaternion.identity);

        this._animationController.play('poke');
        yield* waitForSeconds(0.4);

        this.showBlob = false;
        this.notifyPropertyChanged('showBlob');

        AudioDirector.instance.playOneShot(Sounds.beatGameSFX, true);

        this.descriptionText = this.descriptionString;
        this.awardNameText = this.awardNameString;
        this.notifyPropertyChanged('text');
    }

    private _select = () => {
        this._coroutineManager.addCoroutine(new Coroutine(this._showPresent()));
    };
}
